import { useEffect, useState } from "react";
import { api, ApiError, type Kohde } from "./api";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { EiKohteita } from "./EiKohteita";
import { LinjaLinkki } from "./Linkit";
import { Tilasana } from "./Tila";
import { naytaPvm } from "./pvm";
import { t, virheTeksti } from "./kieli";

/* T514 «Valmiina koeponnistukseen» (Toni 21.10.2026 klo 09.33, 10.35, 20.40, 21.40–22.08; 23.10.2026 klo 05.35 «kohteen vaatimus ensin»).
   Hyväksytty linjatarkastus, ei avointa A-punchia, ei jo hyväksyttyä koeponnistusta, ja NDT täyttyy kun vaatimus on tiedossa.
   Jokaisen portin lähde näkyy: asiakirja / oletus / ei tiedossa; oletusta ei näytetä kunnossa-merkillä ilman merkintää. PED-ristiriidassa
   arvoksi ei valita kumpaakaan, ja molemmat lähteet näkyvät. IV ei ole putkistolle mahdollinen luokka. */
type Rivi = Awaited<ReturnType<typeof api.koeponnistusvalmius>>["rivit"][number];

const LAHDE_SANA: Record<string, string> = { luettelo: "seur.lahde.luettelo", isometri: "seur.lahde.isometri", master: "seur.lahde.master", linja: "seur.lahde.linja" };
/* Sana «master» vain master-adapterin instanssissa (pekilo); muualla neutraali hitsiluettelo (Toni 15.10.2026 klo 18.35: ei sanaa master
   protot'ssa, aaltoflow'ssa eikä demossa — kopiokävelyn sanapyyhkäisy ketjussa 55, 23.10.2026). */
let MASTER = false;
const lahdeSana = (l: string) => t((l === "master" && !MASTER ? "seur.lahde.hitsiluettelo" : LAHDE_SANA[l]) as "seur.lahde.luettelo");

function Ped({ r }: { r: Rivi }) {
  const lahteet = r.ped_lahteet.map((l) => `${lahdeSana(l.lahde)} ${l.arvo ?? "–"}${l.asiakirja ? ` (${l.asiakirja}${l.pvm ? `, ${naytaPvm(l.pvm)}` : ""})` : ""}`).join(" · ");
  const asiakirjat = r.ped_lahteet.filter((l) => (l.lahde === "luettelo" || l.lahde === "isometri") && l.pvm);
  const uusin = asiakirjat.length > 1 ? [...asiakirjat].sort((a, b) => (b.pvm ?? "").localeCompare(a.pvm ?? ""))[0] : null;
  return (
    <span data-ped-tila={r.ped_tila}>
      {r.ped_tila === "asiakirja" ? <b>{r.ped_arvo}</b>
        : r.ped_tila === "ristiriita" ? <Tilasana laji="vika">{t("seur.ped.ristiriita")}</Tilasana>
        : r.ped_tila === "ei_kelpaa" ? <Tilasana laji="vika">{t("seur.ped.ei_kelpaa")}</Tilasana>
        : <Tilasana laji="ei_mitattu">{t("seur.ei_tiedossa")}</Tilasana>}
      {lahteet && <div className="dim">{lahteet}</div>}
      {r.ped_tila === "ristiriita" && uusin && <div className="dim">{t("seur.ped.uudempi", { l: lahdeSana(uusin.lahde) })}</div>}
    </span>
  );
}

function Ndt({ r }: { r: Rivi }) {
  const tila = r.ndt_tila === "valmis" ? (r.ndt_lahde === "oletus" ? <Tilasana laji="varoitus">{t("seur.ndt.valmis_oletus")}</Tilasana> : <Tilasana laji="kunnossa">{t("seur.ndt.valmis")}</Tilasana>)
    : r.ndt_tila === "ei_vaadita" ? <Tilasana laji="kunnossa">{t("seur.ndt.ei_vaadita")}</Tilasana>
    : r.ndt_tila === "vaatimus_ei_tiedossa" ? <Tilasana laji="ei_mitattu">{t("seur.ndt.ei_tiedossa")}</Tilasana>
    : <Tilasana laji="vika">{r.ndt_tila === "kesken" ? t("seur.ndt.kesken") : t("seur.ndt.aloittamatta")}</Tilasana>;
  return (
    <span data-ndt-lahde={r.ndt_lahde} data-ndt-tila={r.ndt_tila}>
      {tila}
      <div className="dim">
        {r.ndt_lahde === "asiakirja" ? t("seur.ndt.lahde_asiakirja") : r.ndt_lahde === "oletus" ? t(MASTER ? "seur.ndt.lahde_oletus" : "seur.ndt.lahde_oletus_hitsiluettelo") : t("seur.ndt.lahde_ei_tiedossa")}
        {r.ndt_erittely ? ` · ${r.ndt_erittely}` : ""}
      </div>
    </span>
  );
}

export default function Koeponnistusvalmius({ masterAdapteri }: { masterAdapteri: boolean }) {
  MASTER = masterAdapteri;
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [vainValmiit, setVainValmiit] = useState(true);
  const [data, setData] = useState<{ kohde: number; rivit: Rivi[]; valmiita: number; hyvaksyttyja: number } | null>(null);
  const [viesti, setViesti] = useState<string | null>(null);
  const kl = useLataus();
  useEffect(() => { kl.seuraa(api.kohteet().then((k) => { setKohteet(k); setKohde((v) => v === "" && k.length ? (k.find((x) => (x.linjoja ?? 0) > 0) ?? k[0]!).id : v); })); }, [kl.kerta]);
  useEffect(() => {
    if (kohde === "") return;
    let elossa = true;
    setViesti(null);
    api.koeponnistusvalmius({ kohde, vain_valmiit: vainValmiit ? "1" : "0" })
      .then((d) => { if (elossa) setData({ ...d, kohde }); })
      .catch((e) => { if (elossa) setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("seur.haku_virhe")); });
    return () => { elossa = false; };
  }, [kohde, vainValmiit]);

  const syy = (s: string | null) => s === "a_punch" ? t("seur.syy.a_punch") : s === "ndt" ? t("seur.syy.ndt") : s === "koeponnistettu" ? t("seur.syy.koeponnistettu") : "";
  return (
    <section className="panel" data-nakyma="koeponnistusvalmius" data-kohde={data?.kohde ?? ""}>
      <div className="panel__head"><h2>{t("valikko.koeponnistusvalmius")}</h2></div>
      <p className="vihje">{t("seur.kp.selite")}</p>
      <LatausTila l={kl} nimi="lataus.kohteet" />
      <EiKohteita haettu={kl.tila === "valmis"} kohteita={kohteet.length} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        {!(ylapalkissa && ylaValittu != null) && <label className="dim">{t("kuittaus.kohde")}<br />
          <select value={kohde} onChange={(e) => setKohde(Number(e.target.value))}>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select></label>}
        <label className="dim" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={vainValmiit} onChange={(e) => setVainValmiit(e.target.checked)} data-suodatin="kp-vain-valmiit" /> {t("nippu.vain_valmiit")}</label>
      </div>
      {data && (
        <p style={{ margin: "8px 0" }} data-kp-valmiita={data.valmiita} data-kp-hyvaksyttyja={data.hyvaksyttyja}>
          {t("seur.kp.maarat", { v: data.valmiita, h: data.hyvaksyttyja })}
        </p>
      )}
      {viesti && <p className="dim" role="status">{viesti}</p>}
      <div className="scroll">
        <table className="tbl" style={{ fontSize: 13 }} data-rivit={data?.rivit.length ?? 0} data-joukko="koeponnistusvalmius">
          <thead><tr><th>{t("nippu.linja")}</th><th>{t("seur.kp.lt_hyvaksytty")}</th><th>{t("seur.kp.a_punch")}</th>
            <th>PED</th><th>NDT</th><th>{t("seur.kp.koeponnistus")}</th><th>{t("seur.kp.valmius")}</th></tr></thead>
          <tbody>{(data?.rivit ?? []).map((r) => (
            <tr key={r.linja_id} data-kp-linja={r.tunnus} data-kp-valmis={r.valmis ? "1" : "0"}>
              <td className="mono"><LinjaLinkki tunnus={r.tunnus} /></td>
              <td>{r.hyvaksytty_pvm ? naytaPvm(r.hyvaksytty_pvm) : "–"}</td>
              <td>{r.avoimia_a_puncheja}</td>
              <td><Ped r={r} /></td>
              <td><Ndt r={r} /></td>
              <td>{r.koeponnistus === "hyvaksytty" ? t("seur.lt.hyvaksytty") : r.koeponnistus === "hylatty" ? t("seur.kp.hylatty")
                : r.koeponnistus ? t("seur.lt.kesken") : "–"}</td>
              <td>{r.valmis ? <Tilasana laji="kunnossa">{t("seur.kp.valmis")}</Tilasana> : <Tilasana laji="varoitus">{syy(r.syy)}</Tilasana>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}
